import * as Sentry from "@sentry/react";
import { browserTracingIntegration, replayIntegration } from "@sentry/react";


/**
 * Capture manual exceptions or events
 * Usage: captureException(error) or captureMessage("message")
 */
export const captureException = (error, context = {}) => {
  //console.log("Capturing exception:", error);
  Sentry.captureException(error, { contexts: { custom: context } });
};

/**
 * Setup global error capture for all errors including those in try-catch
 */
const setupGlobalErrorCapture = () => {
  // Capture unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    // ArcGIS/Esri errors are plain objects, not Error instances — convert them
    // so Sentry shows the actual message and details instead of "[object Object]"
    if (reason && !(reason instanceof Error) && reason.message) {
      const syntheticError = new Error(reason.message);
      syntheticError.name = reason.name || "UnhandledRejection";
      captureException(syntheticError, {
        type: "unhandledRejection",
        esriDetails: reason.details,
      });
    } else {
      captureException(reason, { type: "unhandledRejection" });
    }
  });

  // Capture all errors thrown
  window.addEventListener("error", (event) => {
    captureException(event.error, { type: "uncaughtError" });
  });
};

/**
 * Initialize Sentry error tracking and performance monitoring
 *
 * Environment configurations:
 * - development: DSN is empty, Sentry disabled
 * - testing: Full Sentry integration with testing project
 * - production: Full Sentry integration with production project
 */
export const initSentry = () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || "testing";

  // Only initialize if DSN is provided
  if (!dsn) {
   //console.log(`Sentry disabled for ${environment} environment (no DSN provided)`);
    return;
  }

  Sentry.init({
    dsn,
    environment,
    integrations: [
      browserTracingIntegration(),
      replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSendTransaction(event) {
      const SENTRY_N_PLUS_ONE_API_CALLS = 1008;
      const N_PLUS_ONE_URLS = ["basemaps.arcgis.com", "api.idvilnius.lt/judumas/"];
      const hasNPlusOne = event.performance_issues?.some(
        (issue) =>
          issue.type === SENTRY_N_PLUS_ONE_API_CALLS &&
          N_PLUS_ONE_URLS.some((url) => issue.desc?.includes(url))
      );
      if (hasNPlusOne) return null;
      return event;
    },
  });

 //console.log(`Sentry initialized for ${environment} environment`);
 setupGlobalErrorCapture();
};

/**
 * Capture manual exceptions or events
 * Usage: captureException(error) or captureMessage("message")
 */


/**
 * Set user context for better tracking
 * Usage: setUserContext({ id: "123", email: "user@example.com" })
 */
export const setUserContext = (user) => {
  Sentry.setUser(user);
};

/**
 * Clear user context (e.g., on logout)
 */
export const clearUserContext = () => {
  Sentry.setUser(null);
};

/**
 * Wrapper function to automatically capture errors in async functions
 * Usage: wrap your async function with this
 * Example: const wrappedFn = withSentryCapture(myAsyncFunction);
 *          await wrappedFn(args);
 */
export const withSentryCapture = (fn) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      captureException(error);
      throw error; // re-throw so your code can handle it too
    }
  };
};

/**
 * Setup global fetch interceptor to capture all fetch errors
 * Call this once during app initialization
 */
export const setupGlobalFetchInterceptor = () => {
  const originalFetch = window.fetch;

  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);

      // Capture failed responses (4xx, 5xx)
      if (!response.ok && response.status >= 400) {
        //console.warn(`Fetch error: ${response.status} ${response.statusText}`, args[0]);
      }

      return response;
    } catch (error) {
      // Capture network errors
      captureException(error, {
        type: "fetchError",
        url: args[0],
        method: args[1]?.method || "GET"
      });
      throw error;
    }
  };
};
